import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import TextChangeSchema from "work/proposals/redlining/__diff/_schema/text-change-schema";
import { RedlineAction, RedlineActionKey } from "work/proposals/redlining/changes/redline-change";
import { z } from "zod";
import TextChange from "../../__diff/text-change";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import ProposalFieldSchema from "work/proposals/proposal-fields/_schema/proposal-field-schema";

const RedlineActionKeyZ = z.enum([
  "Accept",
  "Reject",
  "Undo",
  "UndoAdd",
  "UndoEdit",
  "UndoRemove",
  "UndoAccept",
  "UndoReject",
  "Edit",
  "ReEdit",
  "Add",
  "Remove",
] satisfies readonly RedlineActionKey[]);

export type RedlineChangeJSON = z.infer<typeof RedlineChangeSchema.RedlineChangeJSONZ>;
export type RedlineChangeParams = z.infer<typeof RedlineChangeSchema.RedlineChangeZ>;

export default class RedlineChangeSchema {
  public static readonly RedlineChangeJSONZ = z.object({
    field: ProposalFieldSchema.ProposalFieldJSONZ,
    isResolved: z.boolean(),
    action: RedlineActionKeyZ,
    textChanges: z.array(TextChangeSchema.TextChangeJSONZ),
  });

  public static readonly RedlineChangeZ = z.object({
    field: z.instanceof(ProposalField),
    isResolved: z.boolean(),
    get action() {
      return z.instanceof(RedlineAction);
    },
    textChanges: z.array(z.instanceof(TextChange)),
  });

  public static validate(values: unknown): RedlineChangeParams {
    return parseOrThrow(RedlineChangeSchema.RedlineChangeZ, values, {
      logError: true,
      schemaName: "RedlineChangeSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): RedlineChangeJSON {
    return parseOrThrow(RedlineChangeSchema.RedlineChangeJSONZ, json, {
      logError: true,
      schemaName: "RedlineChangeSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is RedlineChangeParams {
    try {
      RedlineChangeSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is RedlineChangeJSON {
    try {
      RedlineChangeSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: RedlineChangeJSON): RedlineChangeParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(RedlineChangeSchema.RedlineChangeJSONZ, json, {
      logError: true,
      schemaName: "RedlineChangeSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return RedlineChangeSchema.codec.decode(parsed);
  }

  public static serialize(params: RedlineChangeParams): RedlineChangeJSON {
    if (isNullOrUndefined(params)) return params;
    const parsed = RedlineChangeSchema.validate(params);
    if (isNullOrUndefined(parsed)) return parsed;
    return RedlineChangeSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(RedlineChangeSchema.RedlineChangeJSONZ, RedlineChangeSchema.RedlineChangeZ, {
    decode: (data) => ({
      field: ProposalField.fromJSON(data.field)!,
      isResolved: data.isResolved,
      action: RedlineAction.fromJSON(data.action),
      textChanges: data.textChanges.map((tc) => new TextChange(TextChangeSchema.deserialize(tc))),
    }),
    encode: (change) => ({
      field: change.field.toJSON(),
      isResolved: change.isResolved,
      action: change.action.toString() as RedlineActionKey,
      textChanges: change.textChanges.map((tc) => TextChangeSchema.serialize(tc)),
    }),
  });
}
